import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import CTAButton from '../components/CTAButton';
import SEOHead from '../components/SEOHead';
import './styles/Forside.css';
import gravearbejdeBillede from '../images/JBC-Entreprenoeren-Gravearbejde.jpg';
import heroMediaImage from '../images/JBC-Entreprenoeren-Jim.jpg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faMapLocationDot, faTruckMoving, faLeaf, faFileSignature, faTractor } from '@fortawesome/free-solid-svg-icons';
import jordvarmeBillede from '../images/Jordvarme.jpg';
import belaegningBillede from '../images/Belægning.jpg';
import midlertidigVejBillede from '../images/MidlertidigVej.jpg';
import fundamenterBillede from '../images/Fundament.jpg';
import tilbygningBillede from '../images/Tilbygning.jpg';
import CTAButtonInvert from '../components/CTAButtonInvert';
import Kontaktformular from '../components/Kontaktformular';
import jbcBillede2 from '../images/jbcBillede2.jpg';
import EmailAndPhoneButton from '../components/EmailAndPhoneButton';

function Forside() {
  const navigate = useNavigate();
  const heroPoster = `${process.env.PUBLIC_URL}/Baggrund/hero-poster.jpg`;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const heroVideo = window.matchMedia('(max-width: 768px)').matches
    ? `${process.env.PUBLIC_URL}/Baggrund/hero-mobil.mp4`
    : `${process.env.PUBLIC_URL}/Baggrund/hero.mp4`;

  const handleCTAClick = () => {
    navigate('/kontakt');
  };

  return (
    <div className="forside">
      <SEOHead
        title="Entreprenør i Holbæk, Tølløse & Roskilde | JBC Entreprenøren"
        description="Lokal entreprenør i Holbæk Kommune siden 2018. Jordvarme, belægning, fundamenter og anlægsarbejde for private og erhverv på hele Sjælland. Gratis tilbud."
        url="/"
        image="https://jbcentreprenoeren.dk/images/og-image.jpg"
      />
      <Header />
      <EmailAndPhoneButton />
      <div className="hero-section">
        <div className="hero-background">
          {reduceMotion ? (
            <img src={heroPoster} alt="" className="hero-image" />
          ) : (
            <video
              className="hero-image"
              src={heroVideo}
              poster={heroPoster}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-hidden="true"
            />
          )}
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-container">
          <div className="hero-grid">
            <div className="hero-text">
              <span className="hero-eyebrow">Velkommen til JBC Entreprenøren</span>
              <h1>Vi leverer et ordentligt stykke arbejde.</h1>
              <p>Specialister i bygge- &amp; anlægsarbejde for private &amp; erhverv på Sjælland.</p>
              <div className="hero-actions">
                <CTAButton text="Få et gratis tilbud" onClick={handleCTAClick} />
                <a href="tel:+4522780476" className="hero-phone-link">
                  <span className="hero-phone-icon">
                    <FontAwesomeIcon icon={faPhone} />
                  </span>
                  <span>
                    <small>Ring til os</small>
                    22 78 04 76
                  </span>
                </a>
              </div>
            </div>
            <div className="hero-media">
              <div className="hero-media-card">
                <img src={heroMediaImage} alt="Jim fra JBC Entreprenøren graver ud med en Hitachi minigraver" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <section className="hvorfor" aria-labelledby="hvorfor-titel">
        <div className="hvorfor-inner">
          <div className="hvorfor-billede">
            <img
              src={gravearbejdeBillede}
              alt="JBC Entreprenørens varevogn, trailer og minigraver på en opgave i et boligområde"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="hvorfor-indhold">
            <span className="hero-eyebrow">Hvorfor vælge os</span>
            <h2 id="hvorfor-titel">En lokal entreprenør, du kan regne med</h2>
            <p className="hvorfor-intro">
              JBC Entreprenøren er et lokalt firma fra Undløse ved Tølløse. Vi løser opgaver for både private og
              erhverv – og vi gør os umage med at levere et ordentligt stykke arbejde hver gang.
            </p>
            <ul className="hvorfor-liste">
              <li>
                <span className="hvorfor-ikon" aria-hidden="true"><FontAwesomeIcon icon={faMapLocationDot} /></span>
                <div>
                  <h3>Lokale siden 2018</h3>
                  <p>Vi kender området omkring Holbæk og Tølløse – både jorden og kommunens krav.</p>
                </div>
              </li>
              <li>
                <span className="hvorfor-ikon" aria-hidden="true"><FontAwesomeIcon icon={faTruckMoving} /></span>
                <div>
                  <h3>Vi kører på hele Sjælland</h3>
                  <p>Har du en opgave længere væk, kommer vi også gerne ud til dig.</p>
                </div>
              </li>
              <li>
                <span className="hvorfor-ikon" aria-hidden="true"><FontAwesomeIcon icon={faFileSignature} /></span>
                <div>
                  <h3>Gratis og uforpligtende tilbud</h3>
                  <p>Det koster ikke noget at spørge. Du får et tilbud, før vi går i gang.</p>
                </div>
              </li>
              <li>
                <span className="hvorfor-ikon" aria-hidden="true"><FontAwesomeIcon icon={faTractor} /></span>
                <div>
                  <h3>Egne maskiner</h3>
                  <p>Vi har selv gravemaskiner og trailer, så der er styr på udstyret fra start.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>
      <div className="tjenester-section">
        <div className="sektion-intro">
          <span className="hero-eyebrow">Det vi laver</span>
          <h2 className="tjenester-titel">Vores Tjenester</h2>
          <p className="tjenester-beskrivelse">JBC Entreprenøren tilbyder en bred vifte af tjenester inden for anlæg og byggeri. Med base i Nordvestsjælland står vores erfarne team klar til at hjælpe dig med dit projekt, uanset hvor på Sjælland du befinder dig.</p>
        </div>
        <div className="tjenester-liste">
          <Link to="/jordvarme" className="tjeneste tjeneste-feature">
            <img className="tjeneste-billede" src={jordvarmeBillede} alt="Gravet rende med jordvarmeslanger lagt ned af JBC Entreprenøren" loading="lazy" decoding="async" />
            <div className="tjeneste-info">
              <span className="tjeneste-tag">Mest efterspurgt</span>
              <h3>Jordvarme</h3>
              <p>Få en energieffektiv og fremtidssikret varmeløsning med professionel installation af jordvarme.</p>
              <span className="tjeneste-link">Se løsningen <span aria-hidden="true">→</span></span>
            </div>
          </Link>
          <Link to="/belægning" className="tjeneste">
            <img className="tjeneste-billede" src={belaegningBillede} alt="Ny fliseterrasse lagt af JBC Entreprenøren" loading="lazy" decoding="async" />
            <div className="tjeneste-info">
              <h3>Belægning</h3>
              <p>Fra indkørsler og terrasser til stier og pladser.</p>
            </div>
          </Link>
          <Link to="/midlertidig-vej" className="tjeneste">
            <img className="tjeneste-billede" src={midlertidigVejBillede} alt="Midlertidig byggevej anlagt med gravemaskine" loading="lazy" decoding="async" />
            <div className="tjeneste-info">
              <h3>Midlertidig Vej</h3>
              <p>Robuste løsninger til vej- og adgangsanlæg.</p>
            </div>
          </Link>
          <Link to="/fundamenter" className="tjeneste">
            <img className="tjeneste-billede" src={fundamenterBillede} alt="Støbt fundament set fra luften" loading="lazy" decoding="async" />
            <div className="tjeneste-info">
              <h3>Fundamenter</h3>
              <p>Solide fundamenter til ethvert byggeri.</p>
            </div>
          </Link>
          <Link to="/tilbygning" className="tjeneste">
            <img className="tjeneste-billede" src={tilbygningBillede} alt="Moderne hus med ny tilbygning" loading="lazy" decoding="async" />
            <div className="tjeneste-info">
              <h3>Tilbygning</h3>
              <p>Udgravning og fundament til din næste tilbygning.</p>
            </div>
          </Link>
        </div>
      </div>
      <section className="lca-teaser">
        <div className="lca-teaser-inner">
          <div className="lca-teaser-tekst">
            <span className="lca-teaser-eyebrow">
              <FontAwesomeIcon icon={faLeaf} /> Bæredygtighed &amp; LCA
            </span>
            <h2>Klar til de nye klimakrav</h2>
            <p>
              Siden 1. juli 2025 skal alt nybyggeri dokumentere sit CO₂-aftryk med en LCA – og byggepladsen har fået
              sin egen grænseværdi. Det er dér, vi som entreprenør gør forskellen.
            </p>
            <ul className="lca-teaser-liste">
              <li>Jord genbruges på grunden, hvor det er muligt</li>
              <li>Planlagt transport og effektivt maskinforbrug</li>
              <li>Dokumentation af forbrug til projektets LCA</li>
            </ul>
            <Link to="/lca" className="lca-teaser-knap">
              Læs om LCA og bæredygtighed <span aria-hidden="true">→</span>
            </Link>
          </div>
          <div className="lca-teaser-kort">
            <span className="lca-teaser-kort-label">Grænseværdi for byggeprocessen</span>
            <div className="lca-teaser-tal">
              1,5
              <span>kg CO₂e/m²/år</span>
            </div>
            <p>Gælder for byggetilladelser søgt fra 1. juli 2025 – og strammes igen i 2027 og 2029.</p>
            <div className="lca-teaser-bar">
              <span style={{ width: '100%' }}>2025 · 1,5</span>
              <span style={{ width: '87%' }}>2027 · 1,3</span>
              <span style={{ width: '73%' }}>2029 · 1,1</span>
            </div>
          </div>
        </div>
      </section>
      <section className="proces" aria-labelledby="proces-titel">
        <div className="proces-inner">
          <span className="hero-eyebrow">Sådan foregår det</span>
          <h2 id="proces-titel">Fra første opkald til færdigt arbejde</h2>
          <ol className="proces-liste">
            <li>
              <span className="proces-nr" aria-hidden="true">1</span>
              <h3>Du kontakter os</h3>
              <p>Ring på <a href="tel:+4522780476">22 78 04 76</a> eller skriv via formularen herunder, og fortæl kort om opgaven.</p>
            </li>
            <li>
              <span className="proces-nr" aria-hidden="true">2</span>
              <h3>Vi tager en snak</h3>
              <p>Vi gennemgår dine ønsker og kigger gerne på stedet, så vi forstår opgaven rigtigt.</p>
            </li>
            <li>
              <span className="proces-nr" aria-hidden="true">3</span>
              <h3>Du får et tilbud</h3>
              <p>Gratis og uforpligtende. Du bestemmer selv, om du vil gå videre.</p>
            </li>
            <li>
              <span className="proces-nr" aria-hidden="true">4</span>
              <h3>Vi udfører arbejdet</h3>
              <p>Vi går i gang som aftalt og leverer et ordentligt stykke arbejde.</p>
            </li>
          </ol>
        </div>
      </section>
      <div className="kontakt-section" style={{backgroundImage: `url(${jbcBillede2})`}}>
        <div className="kontakt-inner">
          <div className="kontakt-content">
            <h2>Lad os drøfte dit projekt</h2>
            <p>Kontakt os for en seriøs og uforpligtende dialog om dine behov. Vi er klar til at levere en professionel løsning.</p>
            <div className="kontakt-info">
              <p><strong>JBC Entreprenøren ApS</strong></p>
              <p>Naurvej 6, Undløse</p>
              <p>4340 Tølløse</p>
              <p>CVR nr. 44384345</p>
              <p>Tlf: 22780476</p>
              <p>Mail: jbc@jbcentreprenoeren.dk</p>
            </div>
          </div>
          <div className="kontakt-formular">
            <Kontaktformular />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Forside;
